import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

/**
 * Konteks tenant dari Control Plane.
 *
 * Sebelum ini, sidebar apps/ui memuat daftar menu HARDCODED dan menulis nama
 * tenant sebagai teks literal ("tenant kemenkum"). Artinya setiap tenant melihat
 * menu yang sama, termasuk untuk fitur yang tidak dibelinya — endpoint-nya
 * memang sudah 403, tetapi pengguna tetap melihat dan mengkliknya.
 *
 * Sekarang semuanya datang dari admin.avio: /me/tenant, /me/navigation, /me/features.
 * CATATAN KEAMANAN: ini lapisan tampilan. Penjaga sebenarnya tetap di server —
 * requireFeature() dan requirePermission() di apps/web.
 */

export interface NavItem { path: string; label: string; icon?: string }
export interface NavGroup { key: string; label: string; items: NavItem[] }

export interface TenantInfo {
  id: string;
  name: string;
  slug: string;
  subdomain: string | null;
  state: string;
  timezone: string;
  locale: string;
  logoUrl: string | null;
  subscription: { packageCode: string; packageName: string; isDemo: boolean; status: string; startsAt: string; endsAt: string | null } | null;
}

export interface TenantCtx {
  status: "loading" | "ready" | "anon" | "error";
  tenant: TenantInfo | null;
  navigation: NavGroup[];
  features: Record<string, boolean>;
  permissions: string[];
  user: { id?: string; name?: string; email?: string } | null;
  error: string | null;
  has: (feature: string) => boolean;
  can: (permission: string) => boolean;
}

const EMPTY: TenantCtx = {
  status: "loading", tenant: null, navigation: [], features: {}, permissions: [], user: null, error: null,
  has: () => false, can: () => false,
};

const Ctx = createContext<TenantCtx>(EMPTY);
export const useTenant = () => useContext(Ctx);

async function getJson<T>(url: string): Promise<T> {
  const r = await fetch(url, { credentials: "same-origin" });
  if (!r.ok) {
    const b = await r.json().catch(() => null);
    throw Object.assign(new Error((b as { error?: string } | null)?.error ?? `Gagal (${r.status})`), { status: r.status });
  }
  return r.json() as Promise<T>;
}

export function TenantProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<Omit<TenantCtx, "has" | "can">>({
    status: "loading", tenant: null, navigation: [], features: {}, permissions: [], user: null, error: null,
  });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [me, tenant, navigation, features] = await Promise.all([
          getJson<{ user: { id: string; name: string; email: string } | null; permissions?: string[] }>("/api/v1/me"),
          getJson<TenantInfo>("/api/v1/me/tenant"),
          getJson<NavGroup[]>("/api/v1/me/navigation"),
          getJson<Record<string, boolean>>("/api/v1/me/features"),
        ]);
        if (!alive) return;
        setState({
          status: "ready", tenant, navigation, features,
          permissions: me.permissions ?? [], user: me.user, error: null,
        });
      } catch (e) {
        if (!alive) return;
        const status = (e as { status?: number }).status;
        setState({
          status: status === 401 ? "anon" : "error",
          tenant: null, navigation: [], features: {}, permissions: [], user: null,
          error: e instanceof Error ? e.message : String(e),
        });
      }
    })();
    return () => { alive = false; };
  }, []);

  /* Favicon ikut whitelabel.
     Gambar dimuat lebih dulu dan href baru dipasang HANYA setelah berhasil:
     favicon yang 404 membuat tab kosong atau ikon rusak — lebih buruk daripada
     tetap memakai lambang AVIO. Kegagalan di sini sengaja tidak berisik. */
  useEffect(() => {
    const url = state.tenant?.logoUrl;
    if (!url) return;
    let batal = false;
    const img = new Image();
    img.onload = () => {
      if (batal) return;
      let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = url;
    };
    img.src = url;
    return () => { batal = true; };
  }, [state.tenant?.logoUrl]);

  const value = useMemo<TenantCtx>(() => ({
    ...state,
    has: (f: string) => state.features[f] === true,
    can: (p: string) => state.permissions.includes(p),
  }), [state]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** Path yang boleh diakses = path yang muncul di navigasi hasil server. */
export function allowedPaths(nav: NavGroup[]): Set<string> {
  return new Set(nav.flatMap((g) => g.items.map((i) => i.path)));
}
